(ns machtfit.product-form.core
  (:require [clojure.string :as str]
            [com.rpl.specter :as sp]
            [machtfit.dashboard-widgets :as widgets]
            [machtfit.datetime :as datetime]
            [machtfit.employee-rules :refer [aggregate-rules]]
            [machtfit.product-form.handlers :as-alias handlers]
            [machtfit.product-form.offer-layers]
            [machtfit.utils :as utils]
            [re-frame.core :as rf]))

(defn ^:export load [js-product-form-data]
  (let [product-form-data (js->clj js-product-form-data :keywordize-keys true)
        machtfit-admin? (-> product-form-data :user :roles (utils/in? "Machtfit-Admin"))
        contains-in? (fn [m [k & ks]]
                       (if (contains? m k)
                         (if (nil? ks)
                           true
                           (recur (get m k) ks))
                         false))
        update-in-maybe (fn [m ks f] (if (contains-in? m ks)
                                       (update-in m ks f)
                                       m))
        expand-number (fn [number]
                        {:choice (if (nil? number) :unset :set)
                         :value number})
        expand-registration-deadline-relative (fn [v]
                                                {:choice (if (or (nil? v) (zero? v)) :unset :set)
                                                 :value v})

        expand-extra-info-fields #(vec (for [x %]
                                         {:value (:question x)
                                          :answers (:answers x)
                                          :required? (:required? x)
                                          :answer-given? (not (empty? (:answers x)))}))

        iso->human #(-> %
                        (update :date datetime/iso-date->date-string)
                        (update :time datetime/iso-time->time-string))
        group-slots-by-day (fn [slots]
                             (let [slot-dates (->> slots (map :date) (apply sorted-set))
                                   slot-groups-by-date (zipmap slot-dates (range (count slot-dates)))]
                               (mapv #(assoc % :group (slot-groups-by-date (:date %))) slots)))

        init-ages (fn [ages]
                    (if (empty? ages)
                      #{:age15-17 :age18-49 :age50-69 :age70}
                      (set (map keyword ages))))

        product (-> product-form-data
                    :product
                    (update-in-maybe [:state] keyword)
                    (update-in-maybe [:what :content-sections] vec)
                    (update-in-maybe [:what :faqs] vec)
                    (update-in-maybe [:what :labels] #(into (sorted-set) (map keyword %)))
                    (update-in-maybe [:what :search-keywords] set)
                    (update-in-maybe [:what :key-facts] set)
                    (update-in-maybe [:when :sessions-per-signup] keyword)
                    (update-in-maybe [:when :cohesion] keyword)
                                        ; expand one (empty) schedule if there are no schedules at all so far
                    (update-in-maybe [:when :schedules] #(if (empty? %) [{:open? true}] %))
                    (update-in-maybe [:when :offer-list-deadline] datetime/iso-date->date-string)
                    (update-in-maybe [:when :slots] group-slots-by-day)
                    (update-in-maybe [:when :slots] #(mapv iso->human %))
                    (update-in-maybe [:when :slots] #(if (empty? %) [{:group 0}] %))
                    (update-in-maybe [:when :registration-deadline-relative] expand-registration-deadline-relative)
                    (update-in-maybe [:for-whom :min-participants] expand-number)
                    (update-in-maybe [:for-whom :max-participants] expand-number)
                    (update-in-maybe [:for-whom :audience-gender] keyword)
                    (update-in-maybe [:for-whom :audience-age] init-ages)
                    (update-in-maybe [:for-whom :allowed-employees] #(-> %
                                                                         (aggregate-rules :employee-group :employee-groups)
                                                                         (aggregate-rules :place-of-business :places-of-business)))
                    (update-in-maybe [:for-whom :area-independent-access] boolean)
                    (update-in-maybe [:where :location-type] keyword)
                    (update-in-maybe [:who :contact-salutation] keyword)
                    (update-in-maybe [:how :registration-type] keyword)
                    (update-in-maybe [:how :extra-info-fields] expand-extra-info-fields)
                    (update-in-maybe [:resale-info :cost-price] widgets/clean-amount)
                    (update-in-maybe [:how-much :amount] widgets/clean-amount)
                    (update-in-maybe [:how-much :suggested-retail-price] widgets/clean-amount)
                    (update-in-maybe [:how-much :vat] #(keyword (str/replace % #"_" "-")))
                    (update-in-maybe [:how-much :max-bookings] expand-number)
                    (update-in-maybe [:how-much :subsidies] #(-> %
                                                                 (aggregate-rules :employee-group :employee-groups)
                                                                 (aggregate-rules :place-of-business :places-of-business)))
                    (update-in-maybe [:visibility :start-date] datetime/iso-date->date-string)
                    (update-in-maybe [:visibility :end-date] datetime/iso-date->date-string)
                    (update-in-maybe [:promotions] (fn [promotions]
                                                     (mapv (fn [promotion]
                                                             (-> promotion
                                                                 (update-in-maybe [:start-date] datetime/iso-date->date-string)
                                                                 (update-in-maybe [:end-date] datetime/iso-date->date-string)
                                                                 (update-in-maybe [:labels] #(into (sorted-set) (map keyword %)))))
                                                           (sort-by #(:end-date %) promotions))))
                    (->> (sp/transform [:when (sp/must :schedules) sp/ALL :registration-deadline] datetime/iso-date->date-string)
                         (sp/transform [:when (sp/must :schedules) sp/ALL :appointments sp/ALL] iso->human)))]
    (rf/dispatch-sync [::handlers/set-io (:io product-form-data)])
    (rf/dispatch-sync [:set-path [:categories] (into (sorted-set) (:categories product-form-data))])
    (rf/dispatch-sync [:set-path [:partner-product-categories] (into (sorted-set) (:partner-product-categories product-form-data))])
    (rf/dispatch-sync [:set-path [:images] (map #(update % :source keyword) (:images product-form-data))])
    (rf/dispatch-sync [::handlers/set-ui (:ui product-form-data)])
    (rf/dispatch-sync [:set-path [:user] (:user product-form-data)])
    (rf/dispatch-sync [:set-path [:certificates]
                       (into {} (for [certificate (:certificates product-form-data)]
                                  [(:id certificate) (update certificate :valid-until-date datetime/iso-date->date-string)]))])
    (rf/dispatch-sync [:set-path [:search-keywords] (:search-keywords product-form-data)])
    (rf/dispatch-sync [:set-path [:key-facts] (:key-facts product-form-data)])
    (rf/dispatch-sync [:set-path [:options] (:options product-form-data)])
    (rf/dispatch-sync [:set-path [:labels-with-groups] (into (sorted-set) (:labels-with-groups product-form-data))])
    (rf/dispatch-sync [::handlers/fetch-suppliers])
    (rf/dispatch-sync [::handlers/set-csrf-token (:csrf-token product-form-data)])
    (when machtfit-admin?
      (rf/dispatch-sync [::machtfit.product-form.offer-layers/fetch-offer-layers-data]))
    (rf/dispatch-sync [::handlers/load-product-defaults])
    (rf/dispatch-sync [:set-path [:changes] (:changes product-form-data)])
    (when product (rf/dispatch-sync [::handlers/load-product product]))))

;; mf.product.validate enforces the same limits, counted in characters
(def rich-text-max-size (* 1000 1000))
(def faqs-max-total-size (* 3 1000 1000))

(defn rich-text-too-big? [text]
  (>= (count text) rich-text-max-size))

(rf/reg-sub ::description-too-big?
  :<- [:get [:product :what :description]]

  (fn [description _]
    (rich-text-too-big? description)))

(rf/reg-sub ::text-too-big?
  (fn [[_ path]]
    (rf/subscribe [:get path]))

  (fn [text _]
    (rich-text-too-big? text)))

(rf/reg-sub ::faq-texts
  :<- [:get [:product :what :faqs]]
  :<- [:get [:product :what :content-sections]]

  (fn [[faqs content-sections] _]
    (map :text (concat faqs (mapcat :faqs content-sections)))))

(rf/reg-sub ::faqs-total-too-big?
  :<- [::faq-texts]

  (fn [texts _]
    (>= (reduce + (map count texts)) faqs-max-total-size)))

(rf/reg-sub ::content-too-big?
  :<- [::description-too-big?]
  :<- [::faq-texts]
  :<- [::faqs-total-too-big?]
  :<- [:get [:product :promotions]]
  :<- [::machtfit-admin?]

  (fn [[description-too-big? faq-texts faqs-total-too-big? promotions machtfit-admin?] _]
    (boolean (or description-too-big?
                 (some rich-text-too-big? faq-texts)
                 faqs-total-too-big?
                 ;; only machtfit admins see and store promotions, and a finished one is read-only;
                 ;; the server checks the same promotions
                 (and machtfit-admin?
                      (some #(and (not (:finished? %))
                                  (not (:deleted? %))
                                  (rich-text-too-big? (:description %)))
                            promotions))))))

(rf/reg-sub ::has-errors?
  :<- [:get [:errors]]

  (fn [errors _]
    (-> errors empty? not)))

(rf/reg-sub ::new?
  :<- [:get [:product :id]]

  (fn [product-id _]
    (nil? product-id)))

(rf/reg-sub ::meta-editable?
  :<- [::new?]
  :<- [:get [:options :can-edit-metadata?]]

  (fn [[new? can-edit-metadata?] _]
    (or new? can-edit-metadata?)))

(rf/reg-sub ::machtfit-admin?
  :<- [:get [:user :roles]]

  (fn [roles _]
    (utils/in? roles "Machtfit-Admin")))

(rf/reg-sub ::machtfit-or-offer-admin?
  :<- [:get [:user :roles]]

  (fn [roles _]
    (or (utils/in? roles "Machtfit-Admin")
        (utils/in? roles "Angebotsadmin"))))

(rf/reg-event-db ::set-promotion-attribute
  (fn [db [_ promotion-index promotion-key promotion-val]]
    (assoc-in db [:product :promotions promotion-index promotion-key] promotion-val)))
